import React from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button, Field, Icon, Sheet, Skeleton, Text, useSheetDismiss } from '../../../../components';
import { Title as SheetTitle } from '@/nova/components/Sheet/Sheet.styles';
import { gameKey, gameTitle, type GameSourceRecord } from '../../game-sources';
import {
  facetLabel,
  gameFilter,
  hasListFilters,
  listFiltersOf,
  providerOptions,
  resolveAutoList,
  sameListFilters,
  themeOptions,
  toggleFacet,
  toggleSubtype,
  toggleType,
  typeBoxState,
  typeOptions,
  NO_LIST_FILTERS,
  type FacetOption,
  type GameFilters,
  type ListFilters,
  type TypeOption,
} from '../../game-filters';
import type { TenantSourceResult } from '../../creatives-tenant-source';
import { adLanguageLabel } from '../../ad-language';
import { firstFree, suggestListName } from '../../list-name';
import { LANGUAGE_NAME_MAX } from '../../list-name-api';
import { collectionPlace, type MarketCollection, type MarketCollectionsAnswer } from '../../market-collections-api';
import {
  sectionOf,
  shapeOf,
  type ListNames,
  type ListShape,
  type OwnListKind,
  type OwnListMode,
} from '../../owner-game-lists-api';
import { providerLabelOf, replaceHead } from '../../site-games';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { ANIM, LIST_WIZARD_ANIM, MOBILE, REDUCED_MOTION } from '@/nova/theme/tokens';
import { useSortableGrid } from '../../hooks/useSortableGrid';
import { useGameSources } from '../../useGameSources';
import { GameThumb } from '../GameThumb';
import { LangChip, LangLine } from '../ListHeading/ListHeading.styles';
import { ListLayoutPicker } from '../ListLayoutPicker';
import { ListWizardStepper, type WizardStep } from '../ListWizardStepper';
import { LumioGlyphOutline } from '../LumioMark';
import { MarketCollectionPicker } from '../MarketCollectionPicker';
import * as S from './CustomListEditor.styles';

/** How many result rows to render before "Load more" (the All catalogue runs to thousands). */
const PAGE = 40;
/** How many of an automatic list's games to show before "Show more": a broad filter matches thousands. */
const AUTO_PAGE = 48;
/** The labels' widths of the options standing in while the catalogue loads, varied as real names are. */
const OPTION_SKELETON_WIDTHS = ['46%', '30%', '58%', '38%', '52%', '34%'];

/** The list as it stands when the editor opens it (editing an existing one), and as it is saved.
 *
 *  It carries game IDS, not game objects, because an id the catalogue cannot resolve (a game the platform
 *  has since delisted) is still in the list. Seeding the editor with resolved games instead left those ids
 *  out of it, and every save had to guess how to put them back. */
export type CustomListDraft = {
  name: string;
  gameIds: string[];
  /** How the list shows on the page: its kind of section and, for a grid, its layout (see `ListShape`). */
  kind: OwnListKind;
  layout: string;
  /** How the list is built. For an automatic list `gameIds` is only the owner's order over what `filters`
   *  match (see `resolveAutoList`); `filters` is null for a hand-picked list. */
  mode: OwnListMode;
  filters: ListFilters | null;
};

/** Each id once, first occurrence kept: the editor keys its tiles by id, and a list holds a game once. */
const uniqueIds = (ids: readonly string[]): string[] => [...new Set(ids.filter((id) => id !== ''))];

export type CustomListEditorProps = {
  /** `new` opens on an empty list, built automatically unless the owner picks by hand; `edit` opens the list. */
  mode: 'new' | 'edit';
  /** The list being edited. Ignored when creating. */
  initial?: CustomListDraft;
  /** Provider id to display name, so each game can say who made it. */
  providerNames: Record<string, string>;
  /** Every list's name on this page but the one being edited, for the suggested name to steer clear of. */
  existingNames?: readonly string[];
  /** Whether the owner picks how the list is laid out. Off where the page draws every list the same way. */
  layoutChoice?: boolean;
  /** Reads the game collections published for the market of the brand's site. Given, a new list may also start from one
   *  of them (a third way on the Method step, saved as a hand-picked list); not given, only the two ways are offered. */
  loadMarketCollections?: (signal: AbortSignal) => Promise<TenantSourceResult<MarketCollectionsAnswer>>;
  /** The languages the list is named in, English first (see `nameLanguagesOf`). With more than one and `translate`, the
   *  Name step offers them under the name as the list's heading does: EN is the name itself, another language the
   *  list's name there, the name translated until the owner types their own. */
  languages?: readonly string[];
  /** The edited list's name in each language; none for a new list. */
  names?: ListNames | null;
  /** The name in each language, null when it could not be had: asked the first time another language than English is
   *  picked, and again once the name has changed. */
  translate?: (name: string) => Promise<ListNames | null>;
  /** `names`: the list's name in each language as the Name step leaves it (see `save`); none when it offered none. */
  onSave: (draft: CustomListDraft, names?: ListNames) => void;
  /** Offered while editing only. */
  onDelete?: () => void;
  /** A save or delete is in flight. Both actions are held off while it is: a new list carries no id, so a
   *  second click would store a SECOND list rather than the same one again. */
  busy?: boolean;
  onClose: () => void;
};

// A new list is a Grid until the owner picks another layout.
const EMPTY: CustomListDraft = {
  name: '',
  gameIds: [],
  kind: 'games_grid',
  layout: '',
  mode: 'auto',
  filters: NO_LIST_FILTERS,
};

/** Stable empty option lists, so a closed facet panel keeps the same identity between renders. */
const NO_OPTIONS: readonly FacetOption[] = [];
const NO_TYPE_OPTIONS: readonly TypeOption[] = [];

/** How a three-state tick reports itself: a type with only some of its subtypes chosen is "mixed", not
 *  "not pressed": otherwise assistive tech cannot tell it from an untouched one. */
const ariaTick = (state: 'on' | 'partial' | 'off'): boolean | 'mixed' =>
  state === 'on' ? true : state === 'partial' ? 'mixed' : false;

type Facet = 'providers' | 'types' | 'themes';

/** A type pick as its chip reads it: `Table · Blackjack` for a subtype, `Slots` for a whole type. */
const typeChipLabel = (key: string): string => key.split(':').map(facetLabel).join(' · ');

/** The Hand-picked card's glyph, a hand, outlined like the hub's own icons: the shared set has none (its `grab` is
 *  a cursor drawn in fixed colours). */
const HandGlyph = (): React.ReactElement => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M9 11V5a1.8 1.8 0 0 1 3.6 0v5" />
    <path d="M12.6 10V8.5a1.8 1.8 0 0 1 3.6 0V11" />
    <path d="M16.2 11a1.8 1.8 0 0 1 3.6 0v3a7 7 0 0 1-7 7h-1a6 6 0 0 1-5-2.7L4 14.5a1.7 1.7 0 0 1 2.8-1.9L9 15" />
  </svg>
);

/** The Provider card's glyph (the wizard's second look), a studio's building, outlined like the hand: the shared set has
 *  no building. */
const StudioGlyph = (): React.ReactElement => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 21h18" />
    <path d="M5 21V8l7-4 7 4v13" />
    <path d="M10 21v-6h4v6" />
  </svg>
);

/** Sets or lifts `inert`, which React 18 does not know as a prop: a step sliding out is out of reach (see the stage).
 *  Two constant callbacks, so React does not detach and attach them again on every render. */
const inertWhen =
  (on: boolean) =>
  (el: HTMLDivElement | null): void => {
    if (!el) return;
    if (on) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  };
const INERT = inertWhen(true);
const NOT_INERT = inertWhen(false);

/**
 * Build or edit one of the owner's own game lists, as a three-step wizard in one sheet: Method, Games, Name and
 * layout. One editor state spans the steps, so moving between them never loses a pick, a filter, the order or the
 * name. A new list starts on Method and moves on by Continue (Games refuses until the list holds something); an
 * edit opens on Games, and every step is a tab.
 *
 * The list is built in one of two ways, over one filter bar that is the same in both (the concept's "B2"):
 *
 *  - **Automatic, by filters**: the list is every catalogue game the filters match, and games the platform
 *    adds later that match join it on their own. The editor shows them as they would land, and they drag into
 *    the owner's order as a hand-picked list's do.
 *  - **Hand-picked**: the results on the left (the filters, plus a search by name) Add and Added into the list
 *    on the right, whose games drag into their order and come out with their X.
 */
export const CustomListEditor = ({
  mode,
  initial,
  providerNames,
  existingNames = [],
  layoutChoice = true,
  loadMarketCollections,
  languages = [],
  names = null,
  translate,
  onSave,
  onDelete,
  busy = false,
  onClose,
}: CustomListEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  // On a phone a facet's panel spans the bar; elsewhere it hangs from its button.
  const isPhone = useMediaQuery(MOBILE);
  const editing = mode === 'edit';
  const start = editing && initial ? initial : EMPTY;
  const startAuto = start.mode === 'auto';

  // The wizard's step: a new list starts on its method, an edit on its games.
  const [step, setStep] = React.useState<WizardStep>(editing ? 2 : 1);
  // The wizard's second look (the `list-wizard-v2` flag): the steps as pills with a slide between them, and the
  // automatic Games step as three filter cards.
  const { isEnabled } = useFeatureGate();
  const v2 = isEnabled('list-wizard-v2');
  const stillMotion = useMediaQuery(REDUCED_MOTION);
  // With v2, the step sliding out while the next slides in, and the way both go (a step further on comes in from the
  // right). Neither on the first render, which does not slide, nor with motion reduced. The step on show slides in
  // for as long as there is one sliding out, and is at rest otherwise.
  const [leaving, setLeaving] = React.useState<{ step: WizardStep; dir: S.StepDir } | null>(null);
  // The matching games rise into place when a pick brings them (v2), not whenever the Games step mounts (an edit
  // opening on them, Back from Name): set by a pick (numbered, so a pick during the last one's rise holds it on for
  // its own), dropped once the rise has played or the step changes.
  const [risePick, setRisePick] = React.useState<number | null>(null);
  const risePicked = risePick !== null;
  const risePickSeq = React.useRef(0);
  // Why the last Continue was refused, already translated. Cleared on any step change, shown only while the list
  // is still empty.
  const [notice, setNotice] = React.useState<string | null>(null);
  // A Save without a name, asked for from another step than the name's (see the effect that answers it).
  const [pendingNameFlash, setPendingNameFlash] = React.useState(false);
  const [name, setName] = React.useState(start.name);
  // Whether the owner has made the name their own: a new automatic list's name follows its filters until then. An
  // edited list's name never follows.
  const [nameTouched, setNameTouched] = React.useState(editing);
  // The name in each website language (the Name step's chips): the language the field edits (null: the name itself,
  // EN's chip), what the owner typed in the others, and what `translate` gave for one name (`for`; null while it is
  // asked). The edited list's own names stay its own name's (see `namesNow`).
  const perLanguage = translate !== undefined && languages.length > 1;
  const [lang, setLang] = React.useState<string | null>(null);
  const [typedNames, setTypedNames] = React.useState<ListNames>({});
  const [shownNames, setShownNames] = React.useState<{ for: string; names: ListNames | null } | null>(null);
  const storedNames = editing ? names : null;
  // The name last asked for: an answer for an earlier one is dropped.
  const askedFor = React.useRef<string | null>(null);
  const startShape = shapeOf(start.kind, start.layout);
  const [shape, setShape] = React.useState<ListShape>(startShape);
  const [listMode, setListMode] = React.useState<OwnListMode>(start.mode);
  const auto = listMode === 'auto';
  // A new list may start from a collection of the brand's market, when the page can read them: a hand-picked list that
  // holds the collection's games, in its order, until the owner changes them. Its step 2 is picking that collection
  // (`pickedCollection`), and Continue goes on to its name and layout; the games are edited later, like any list's.
  const offerMarket = !editing && loadMarketCollections !== undefined;
  const [fromMarket, setFromMarket] = React.useState(false);
  const market = offerMarket && fromMarket && !auto;
  const [pickedCollection, setPickedCollection] = React.useState<MarketCollection | null>(null);
  // A hand-picked list's game ids, in order. Every stored id is here, including those the catalogue cannot
  // resolve, which render as placeholders the owner can see, move and remove.
  const startIds = React.useMemo(() => uniqueIds(startAuto ? [] : start.gameIds), [startAuto, start.gameIds]);
  const [picked, setPicked] = React.useState<string[]>(startIds);
  // An automatic list's order: the owner's order over what its filters match (see `resolveAutoList`), which a
  // drag here sets as it does in the Games tab. Games that join later land after it.
  const startAutoOrder = React.useMemo(() => (startAuto ? uniqueIds(start.gameIds) : []), [startAuto, start.gameIds]);
  const [autoOrder, setAutoOrder] = React.useState<string[]>(startAutoOrder);
  const startFilters = React.useMemo(
    () => listFiltersOf(startAuto && start.filters ? start.filters : NO_LIST_FILTERS),
    [startAuto, start.filters],
  );
  // The filters, one set for both ways of building: switching between them keeps what is picked. The search by
  // name narrows the hand-picked results only; it is never part of an automatic list's rule.
  const [filters, setFilters] = React.useState<GameFilters>({ ...startFilters, query: '' });
  const rule = React.useMemo(() => listFiltersOf(filters), [filters]);
  const hasRule = hasListFilters(rule);
  // Which facet's panel is open, where it hangs, and which type has its subtypes shown.
  const [menu, setMenu] = React.useState<Facet | null>(null);
  const [menuLeft, setMenuLeft] = React.useState(0);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  // Searching WITHIN the open panel: 19 providers and 81 themes are too many to scan by eye.
  const [menuQuery, setMenuQuery] = React.useState('');
  const barRef = React.useRef<HTMLDivElement>(null);
  // Sending the owner back to the name field when the confirm button cannot act yet.
  const nameWrapRef = React.useRef<HTMLDivElement>(null);
  const [nameFlash, setNameFlash] = React.useState(false);
  const flashTimer = React.useRef<number | null>(null);
  const [visible, setVisible] = React.useState(PAGE);
  const [autoVisible, setAutoVisible] = React.useState(AUTO_PAGE);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  // The game added last: the list scrolls to it and its card flashes, so a pick made on the left is
  // visibly answered on the right even when the list is long enough to have scrolled away from it.
  // Held as a fresh object every time rather than as a bare id: that identity is what makes re-adding the
  // SAME game register, where the id alone is no change and neither the scroll nor the flash would run again.
  const [flash, setFlash] = React.useState<{ id: string } | null>(null);
  // Each add also sends a copy of the list's count growing and fading off it, which registers the add even where
  // the list is out of sight (a phone's closed segment). Numbered, so an add during the last one's replays it;
  // cleared once it has had time to play (on a timer, not on its animation's end, which never comes when the
  // count is unmounted mid-way or motion is reduced), so the count remounting never replays it.
  const [countPing, setCountPing] = React.useState<number | null>(null);
  const pingSeq = React.useRef(0);
  const slotEls = React.useRef(new Map<string, HTMLDivElement>());
  // The editor is sized to the room the sheet actually leaves it, so the sheet's own scroll area never has to
  // scroll on top of the panes, which scroll their own lists. Measured rather than guessed: it depends on the
  // sheet's height cap, its header and its footer.
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = React.useState<number | null>(null);
  // On a phone a hand-picked list's results and its games are the two segments of an accordion, one open at a
  // time, that fills the sheet's scroll area: that room is measured so it fits it exactly.
  const [splitHeight, setSplitHeight] = React.useState<number | null>(null);
  const [segment, setSegment] = React.useState<'results' | 'selected'>('results');
  const segmentId = React.useId();
  // The method step's question, which names its group of two ways.
  const methodHeadingId = React.useId();
  // What names each way's radio (its card's name alone) and what describes it (the rest of the card).
  const modeId = React.useId();
  // The heading of the step on show, where Continue, Back and the stepper leave the keyboard (see jumpTo).
  const stepHeadRef = React.useRef<HTMLHeadingElement>(null);
  // When Continue last moved the step on (its press's time), for the double tap the way on ignores (see wayOn).
  const movedOnAt = React.useRef<number | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // The whole catalogue: the filters narrow it, so no curated browse source is needed. Try again reads it again after a
  // read that failed. It is the SLIM catalogue (no art): it alone says which games exist, and every game drawn has its
  // art read by id (GameThumb), so a tile is never called missing while its art is still on its way.
  const [catalogAttempt, setCatalogAttempt] = React.useState(0);
  const { games, loading, failed } = useGameSources('all', true, catalogAttempt);
  const gameById = React.useMemo(() => new Map(games.map((game) => [gameKey(game), game])), [games]);

  // A refusal on the market step answers the catalogue as it stood when Continue was pressed: once that read fails, or
  // is tried again, it gives way to the hint for the catalogue as it stands now (see `waiting`).
  React.useEffect(() => {
    if (market) setNotice(null);
  }, [market, failed]);

  // What the list held when it opened, so an edit can mark what is new and sum up the changes.
  const startedWith = React.useMemo(() => new Set(startIds), [startIds]);

  React.useEffect(() => {
    setVisible(PAGE);
    setAutoVisible(AUTO_PAGE);
  }, [filters]);

  // On a phone a hand-picked list's Games step is the accordion, measured as it arrives (see the effect below).
  const accordion = step === 2 && isPhone && !auto;

  React.useLayoutEffect(() => {
    const editor = editorRef.current;
    const body = editor?.parentElement;
    if (!editor || !body) return undefined;
    // The sheet's scroll area: the nearest ancestor that scrolls.
    let area: HTMLElement | null = body;
    while (area && getComputedStyle(area).overflowY !== 'auto') area = area.parentElement;
    if (!area) return undefined;
    const scrollArea = area;
    const measure = (): void => {
      const pad = getComputedStyle(body);
      const inset = parseFloat(pad.paddingTop || '0') + parseFloat(pad.paddingBottom || '0');
      // Room left over once the body's own padding is taken; never smaller than a usable editor (its head and
      // filter bar, with a few rows of the list under them).
      setEditorHeight(Math.max(360, scrollArea.clientHeight - inset));
      // The accordion's room on a phone: the whole scroll area less the sheet's padding under it. Exactly that,
      // so the sheet scrolls just far enough for the accordion to reach the top, and not a pixel further. Never
      // too small to use. Read only while the sheet is at its cap, its content running past it: short of the cap
      // the scroll area is only as tall as what it holds, and that height fed back to the accordion grew the sheet
      // a little on every frame until it met the cap. Unread, the accordion is a screen tall, which takes the sheet
      // to its cap at once (a window grown taller leaves it short of the cap, and so unread again).
      setSplitHeight(
        scrollArea.scrollHeight > scrollArea.clientHeight
          ? Math.max(360, scrollArea.clientHeight - parseFloat(pad.paddingBottom || '0'))
          : null,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollArea);
    return () => observer.disconnect();
    // Runs again as the accordion arrives, so it is read before its first frame rather than after it.
  }, [accordion]);

  // A facet's panel opens whole on screen: the sheet scrolls it into view (on a short screen, a phone's above all,
  // its foot reaches past the sheet's). Synchronises with the scroll area, after the panel is in the DOM.
  React.useEffect(() => {
    if (!menu) return undefined;
    const frame = window.requestAnimationFrame(() => menuRef.current?.scrollIntoView({ block: 'nearest' }));
    return () => window.cancelAnimationFrame(frame);
  }, [menu]);

  // Synchronises with a timer: a pick's rise is dropped once it has played.
  React.useEffect(() => {
    if (risePick === null) return undefined;
    const timer = window.setTimeout(() => setRisePick(null), LIST_WIZARD_ANIM.RISE_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [risePick]);

  // Synchronises with a timer: the step sliding out is taken away once it has slid.
  React.useEffect(() => {
    if (!leaving) return undefined;
    const timer = window.setTimeout(() => setLeaving(null), LIST_WIZARD_ANIM.STEP_SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  // Synchronises with a timer: the count's copy is taken away once it has played.
  React.useEffect(() => {
    if (countPing === null) return undefined;
    const timer = window.setTimeout(() => setCountPing(null), S.COUNT_PING_MS);
    return () => window.clearTimeout(timer);
  }, [countPing]);

  // Runs after the new card is in the DOM, so there is something to scroll to.
  React.useEffect(() => {
    if (!flash) return undefined;
    slotEls.current.get(flash.id)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const timer = window.setTimeout(() => setFlash(null), S.FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // An open facet panel closes on a click outside the bar and on Escape, as a menu does. Synchronises with the
  // document, the only thing that sees a click landing elsewhere.
  React.useEffect(() => {
    if (!menu) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) setMenu(null);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setMenu(null);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [menu]);

  const pickedIds = React.useMemo(() => new Set(picked), [picked]);
  // Dragging reorders the list right here, so the owner does not have to save and then reorder in the tab. On a touch
  // screen a game is lifted by resting a finger on it (a touch never sends a mouse's drag events).
  const sortable = useSortableGrid(picked, setPicked, { longPress: true });

  // What the facets count against: the search by name is the hand-picked results' only.
  const browsing = React.useMemo<GameFilters>(() => (auto ? { ...filters, query: '' } : filters), [auto, filters]);

  // Each facet's options, counted as they WOULD land (the other facets applied, this one left open).
  // Built only for the panel that is actually open: every one of these walks the whole catalogue, and
  // the filters carry the search box, so computing all three on every keystroke meant three full passes
  // per character whose results nothing on screen was reading.
  const providers = React.useMemo(
    () => (menu === 'providers' ? providerOptions(games, browsing, providerNames, t) : NO_OPTIONS),
    [menu, games, browsing, providerNames, t],
  );
  const types = React.useMemo(
    () => (menu === 'types' ? typeOptions(games, browsing, providerNames, t) : NO_TYPE_OPTIONS),
    [menu, games, browsing, providerNames, t],
  );
  const themes = React.useMemo(
    () => (menu === 'themes' ? themeOptions(games, browsing, providerNames, t) : NO_OPTIONS),
    [menu, games, browsing, providerNames, t],
  );

  // The hand-picked results, and the automatic list as it would land.
  const matches = React.useMemo(
    () => (auto ? [] : games.filter(gameFilter(filters, providerNames, t))),
    [auto, games, filters, providerNames, t],
  );
  const autoGames = React.useMemo<GameSourceRecord[]>(
    () => (auto ? resolveAutoList(games, rule, autoOrder) : []),
    [auto, games, rule, autoOrder],
  );
  const facetCount = auto ? autoGames.length : matches.length;
  // The automatic list as it opened, to tell a reorder here from the filters bringing other games.
  const startAutoIds = React.useMemo(
    () => (startAuto ? resolveAutoList(games, startFilters, startAutoOrder).map(gameKey) : []),
    [startAuto, games, startFilters, startAutoOrder],
  );

  const facetTitle = (id: Facet): string => (id === 'providers' ? 'Provider' : id === 'types' ? 'Type' : 'Theme');

  const facetButtons = [
    { id: 'providers' as const, label: facetTitle('providers'), picked: filters.providers.length },
    { id: 'types' as const, label: facetTitle('types'), picked: filters.types.length },
    { id: 'themes' as const, label: facetTitle('themes'), picked: filters.themes.length },
  ];

  const setFacet = (id: Facet, values: string[]): void => {
    setFilters((prev) => ({ ...prev, [id]: values }));
    risePickSeq.current += 1;
    setRisePick(risePickSeq.current);
    // In the second look's automatic Games step a refusal answers the press that made it: once the picks change, the
    // footer's hint for the picks as they are takes its place (see `waiting`), and the old words are not read out
    // again. A hand-picked list has no hint, so its refusal stays until games are added; a list from the market has
    // no filters to change.
    if (v2 && auto) setNotice(null);
  };

  // Every pick as a chip that takes it off again.
  /** One pick as the owner reads it: a provider by its name, a type as `Table · Blackjack`, a theme by its label. */
  const pickLabel = (id: Facet, value: string): string =>
    id === 'providers'
      ? providerNames[value] ?? t('Provider {{id}}', { id: value })
      : id === 'types'
        ? typeChipLabel(value)
        : facetLabel(value);

  const chips = [
    ...filters.providers.map((value) => ({
      key: `p:${value}`,
      label: pickLabel('providers', value),
      remove: () => setFacet('providers', toggleFacet(filters.providers, value)),
    })),
    ...filters.types.map((value) => ({
      key: `y:${value}`,
      label: pickLabel('types', value),
      remove: () => setFacet('types', toggleFacet(filters.types, value)),
    })),
    ...filters.themes.map((value) => ({
      key: `t:${value}`,
      label: pickLabel('themes', value),
      remove: () => setFacet('themes', toggleFacet(filters.themes, value)),
    })),
  ];

  const renderBox = (state: 'on' | 'partial' | 'off'): React.ReactElement => (
    <S.Box $state={state} aria-hidden="true">
      {state === 'on' ? <Icon name="check" size={11} /> : null}
      {state === 'partial' ? <Icon name="minus" size={11} /> : null}
    </S.Box>
  );

  // The catalogue still on its way: a facet's options are not known yet, which is not the same as none.
  const catalogPending = loading && games.length === 0;

  /** Options standing in while the catalogue loads, shaped as the real ones (a tick, a label, a count). */
  const renderOptionSkeleton = (): React.ReactElement => (
    <>
      {OPTION_SKELETON_WIDTHS.map((width, i) => (
        <S.OptSkeleton key={i} data-testid="option-skeleton" aria-hidden="true">
          <Skeleton width={16} height={16} radius={5} />
          <Skeleton width={width} height={12} radius={6} />
          <S.OptSkeletonCount width={24} height={10} radius={5} />
        </S.OptSkeleton>
      ))}
    </>
  );

  const renderFlatOptions = (id: 'providers' | 'themes', options: readonly FacetOption[]): React.ReactElement => {
    const q = menuQuery.trim().toLowerCase();
    const shownOptions = q === '' ? options : options.filter((o) => o.label.toLowerCase().includes(q));
    return (
      <>
        <S.MenuSearch
          type="search"
          value={menuQuery}
          onChange={(e) => setMenuQuery(e.target.value)}
          placeholder={id === 'themes' ? t('Search themes') : t('Search providers')}
          aria-label={id === 'themes' ? t('Search themes') : t('Search providers')}
        />
        {catalogPending ? renderOptionSkeleton() : null}
        {!catalogPending && shownOptions.length === 0 ? <S.Note text="No matches." /> : null}
        {shownOptions.map((option) => {
          const on = filters[id].includes(option.value);
          return (
            <S.Opt
              key={option.value}
              type="button"
              aria-pressed={on}
              onClick={() => setFacet(id, toggleFacet(filters[id], option.value))}
            >
              {renderBox(on ? 'on' : 'off')}
              <S.OptLabel text={option.label} raw />
              <S.OptCount>{option.count}</S.OptCount>
            </S.Opt>
          );
        })}
      </>
    );
  };

  const renderTypeOptions = (): React.ReactElement[] =>
    types.map((type: TypeOption) => {
      const open = expanded === type.value;
      return (
        <React.Fragment key={type.value}>
          <S.OptRow>
            <S.Opt
              type="button"
              $fill
              aria-pressed={ariaTick(typeBoxState(filters.types, type))}
              onClick={() => setFacet('types', toggleType(filters.types, type))}
            >
              {renderBox(typeBoxState(filters.types, type))}
              <S.OptLabel text={type.label} raw />
              <S.OptCount>{type.count}</S.OptCount>
            </S.Opt>
            {type.subs.length > 0 ? (
              <S.ExpandBtn
                type="button"
                $open={open}
                aria-expanded={open}
                aria-label={t('Show the kinds of {{name}}', { name: type.label })}
                onClick={() => setExpanded(open ? null : type.value)}
              >
                <Icon name="chevron-down" size={13} />
              </S.ExpandBtn>
            ) : null}
          </S.OptRow>
          {open ? (
            <S.SubList>
              {type.subs.map((sub) => {
                const on = filters.types.includes(sub.value);
                return (
                  <S.Opt
                    key={sub.value}
                    type="button"
                    $sub
                    aria-pressed={on}
                    onClick={() => setFacet('types', toggleSubtype(filters.types, type, sub.value))}
                  >
                    {renderBox(on ? 'on' : 'off')}
                    <S.OptLabel text={sub.label} raw />
                    <S.OptCount>{sub.count}</S.OptCount>
                  </S.Opt>
                );
              })}
            </S.SubList>
          ) : null}
        </React.Fragment>
      );
    });

  const shown = matches.slice(0, visible);
  // Memoised: its ids are what the drag hook re-measures on, and a new array every render (a keystroke in the name)
  // would re-measure every tile and cut short the glide of the last move.
  const autoShown = React.useMemo(() => autoGames.slice(0, autoVisible), [autoGames, autoVisible]);
  const autoShownIds = React.useMemo(() => autoShown.map(gameKey), [autoShown]);
  // A drag reorders the games on show, the head of the list; the rest keep their order after them.
  const reorderAuto = React.useCallback(
    (head: string[]): void => setAutoOrder(replaceHead(autoGames.map(gameKey), head)),
    [autoGames],
  );
  const autoSortable = useSortableGrid(autoShownIds, reorderAuto, { longPress: true });
  // Results not yet in the list, which "Add all" would add. Offered once the results are narrowed at all: over
  // the whole catalogue it would be thousands of games in one click.
  const notInList = matches.filter((game) => !pickedIds.has(gameKey(game)));
  const canAddAll = notInList.length > 1 && (hasRule || filters.query.trim() !== '');

  const pingCount = (): void => {
    pingSeq.current += 1;
    setCountPing(pingSeq.current);
  };

  /** In or out of the list. */
  const toggleGame = (id: string): void => {
    if (pickedIds.has(id)) {
      setPicked((prev) => prev.filter((picks) => picks !== id));
      return;
    }
    setPicked((prev) => [...prev, id]);
    pingCount();
    // On a phone the list is the accordion's other, closed segment: the pick shows on its row and in the count,
    // and nothing opens or scrolls to it.
    if (!isPhone) setFlash({ id });
  };

  const addAll = (): void => {
    const ids = notInList.map(gameKey);
    if (ids.length === 0) return;
    setPicked((prev) => [...prev, ...ids]);
    pingCount();
    if (!isPhone) setFlash({ id: ids[0] });
  };

  /** A collection of the market starts the list: the games of it the catalogue carries, in its order, replace what the
   *  list held, and its name (made free on the page, as a suggestion is) becomes the list's until the owner types one
   *  of their own. */
  const pickCollection = (next: MarketCollection, ids: string[]): void => {
    setPickedCollection(next);
    setPicked(uniqueIds(ids));
    if (!nameTouched) setName(firstFree(next.name, existingNames));
    setNotice(null);
  };

  /** Opens one of the phone accordion's segments, which closes the other. A facet's panel belongs to the
   *  results, so it closes with them. */
  const openSegment = (next: 'results' | 'selected'): void => {
    setSegment(next);
    setMenu(null);
  };

  // An automatic list's name, suggested from its filters (English on purpose: it is the title the site shows, so a
  // provider nobody named reads as the chip's fallback in English, not in the hub's language). A new list's name
  // follows the suggestion until the owner types, and a field they leave empty falls back to it, so an automatic list
  // is never without a name.
  const suggestion = auto ? suggestListName(rule, (id) => providerNames[id] ?? `Provider ${id}`, existingNames) : '';
  const following = auto && !editing && !nameTouched;
  const shownName = following ? suggestion : name;
  const effectiveName = following
    ? suggestion
    : name.trim() !== ''
      ? name.trim()
      : auto && !editing
        ? suggestion
        : '';

  // The names shown for the name as it stands: what was translated for it (none while asked, or when that was another
  // name's), the edited list's own names over it while the name is the list's own. Then the owner's own words.
  const translatedNow = shownNames !== null && shownNames.for === effectiveName ? shownNames.names : null;
  const namesNow =
    storedNames && effectiveName === start.name ? { ...(translatedNow ?? {}), ...storedNames } : translatedNow;
  const asking = shownNames !== null && shownNames.for === effectiveName && shownNames.names === null;
  const ownNames: ListNames = Object.fromEntries(
    Object.entries(typedNames)
      .map(([code, value]) => [code, value.trim()] as const)
      .filter(([, value]) => value !== ''),
  );
  /** The name in another language as the field shows it: the owner's own, else the name's there (nothing while it is
   *  asked), else the name itself, as the site shows a language with no name of its own. */
  const nameIn = (code: string): string => typedNames[code] ?? (namesNow?.[code] || (asking ? '' : effectiveName));

  // A language's name landing after its pick (asked then: its translation, or the name itself when none came) is
  // selected as at the pick, so typing replaces it. Keyed on what that language's field shows, which is empty only
  // while its own name is awaited: an answer for other languages leaves the field the owner is in as it is. Once the
  // owner types there it is theirs.
  const landed = lang !== null && typedNames[lang] === undefined ? nameIn(lang) : undefined;
  React.useLayoutEffect(() => {
    if (!landed) return;
    const input = nameWrapRef.current?.querySelector('input');
    if (input && document.activeElement === input) input.select();
  }, [landed]);

  /** The field edits another language (null: the name itself). Another language than English shows the name
   *  translated: asked the first time one is picked, and again once the name has changed; the list's own name in a
   *  language it has a name in asks nothing. The field takes the keyboard, its text selected, so typing replaces it. */
  const pickLanguage = (code: string | null): void => {
    setLang(code);
    window.requestAnimationFrame(() => {
      const input = nameWrapRef.current?.querySelector('input');
      input?.focus();
      input?.select();
    });
    if (code === null || !translate || effectiveName === '' || shownNames?.for === effectiveName) return;
    if (storedNames?.[code] && effectiveName === start.name) return;
    const forName = effectiveName;
    askedFor.current = forName;
    setShownNames({ for: forName, names: null });
    void translate(forName)
      .catch(() => null)
      .then((got) => {
        if (askedFor.current === forName) setShownNames({ for: forName, names: got ?? {} });
      });
  };

  // What changed since the editor opened, for the line beside Save.
  const renamed = effectiveName !== start.name;
  // A language typed otherwise than the list has it is a change, as a rename is.
  const renamedIn = Object.entries(ownNames).some(([code, value]) => value !== names?.[code]);
  const reshaped = shape !== startShape;
  const changes: string[] = [];
  if (listMode !== start.mode) {
    changes.push(auto ? t('now automatic') : t('now hand-picked'));
  } else if (auto) {
    if (!sameListFilters(rule, startFilters)) changes.push(t('filters changed'));
    else if (autoGames.map(gameKey).join('\u0000') !== startAutoIds.join('\u0000')) changes.push(t('reordered'));
  } else {
    const added = picked.filter((id) => !startedWith.has(id)).length;
    const removed = startIds.filter((id) => !pickedIds.has(id)).length;
    // Order counts as a change too, or a drag would leave Save off. Compared over the games BOTH versions
    // hold, so adding or removing one is not also reported as a reorder.
    const keptThen = startIds.filter((id) => pickedIds.has(id));
    const keptNow = picked.filter((id) => startedWith.has(id));
    const reordered = keptThen.join('\u0000') !== keptNow.join('\u0000');
    if (added) changes.push(t('{{n}} added', { n: added }));
    if (removed) changes.push(t('{{n}} removed', { n: removed }));
    if (reordered) changes.push(t('reordered'));
  }
  if (renamed || renamedIn) changes.push(t('renamed'));
  if (reshaped) {
    changes.push(shape === 'row' ? t('now a row') : shape === 'mixed' ? t('now a mixed grid') : t('now a grid'));
  }
  const dirty = changes.length > 0;

  const named = effectiveName.length > 0;
  // What the list holds: a hand-picked list its games; an automatic one what its filters match, which needs a
  // filter (with none it would be the whole catalogue) and a match; a list from the market its collection's games,
  // which needs a collection picked (games picked by hand before do not stand in for one).
  const filled = auto
    ? hasRule && autoGames.length > 0
    : market
      ? pickedCollection !== null && picked.length > 0
      : picked.length > 0;
  // A list always needs a name and something in it. Emptying one in the editor is not a way to save an
  // empty list, it is a way to delete it (which Delete does). Editing additionally needs a real change.
  const canSave = named && filled && (editing ? dirty : true) && !busy;

  /** Sends attention to the name field and flashes it. Restarts the flash even while one is running:
   *  setting an already-true flag changes nothing, so a second click would answer nothing at all. */
  const flashName = React.useCallback((): void => {
    nameWrapRef.current?.querySelector('input')?.focus();
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    setNameFlash(false);
    window.requestAnimationFrame(() => setNameFlash(true));
    flashTimer.current = window.setTimeout(() => setNameFlash(false), S.FLASH_MS);
  }, []);
  React.useEffect(
    () => () => {
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  // The name field mounts with step 3, and focusing it is a DOM effect: a flash asked for from another step runs
  // once the field is there.
  React.useEffect(() => {
    if (step === 3 && pendingNameFlash) {
      setPendingNameFlash(false);
      flashName();
    }
  }, [step, pendingNameFlash, flashName]);

  const goTo = (next: WizardStep): void => {
    if (v2 && !stillMotion && next !== step) setLeaving({ step, dir: next > step ? 'forward' : 'back' });
    setRisePick(null);
    setStep(next);
    setNotice(null);
    // The Name step opens on the name itself (EN): the filters may have changed it since a language was picked.
    setLang(null);
    // A facet's panel is the Games step's: left open, it would take the next step's first Escape (see its effect).
    setMenu(null);
  };

  /** Continue, Back and the stepper. The control used is not always on the step it leads to (Back on Method, a step
   *  behind that becomes the current one), and focus on a node taken out of the DOM falls to <body>: the keyboard goes
   *  to the new step's heading instead, the step rendered first so that its heading is there to take it. */
  const jumpTo = (next: WizardStep): void => {
    if (next === step) return;
    flushSync(() => goTo(next));
    stepHeadRef.current?.focus();
  };

  /** Add's way on. Self-gates like Save: the Games step refuses until the list holds something, and says why. Moving
   *  on takes the keyboard to the new step's heading (see jumpTo), so a second or held Enter cannot press the next
   *  step's button, which is this one, and keeps the press's time (`at`) for a second tap (see wayOn). */
  const continueStep = (at: number): void => {
    if (step === 1) {
      movedOnAt.current = at;
      jumpTo(2);
      return;
    }
    if (!filled) {
      // A catalogue that could not be read matches nothing, offers no filter and lets no collection be picked: that is
      // what stands in the way.
      setNotice(
        auto
          ? failed
            ? t("Couldn't load")
            : hasRule
              ? t('No games match these filters yet')
              : t('Choose at least one filter')
          : market && pickedCollection === null
            ? failed
              ? t("Couldn't load")
              : t('Pick a collection to start from')
            : t('Add at least one game'),
      );
      return;
    }
    movedOnAt.current = at;
    jumpTo(3);
  };

  const back = (): void => jumpTo((step - 1) as WizardStep);

  /** The handler self-gates: the button only LOOKS unavailable, so it still has to refuse properly. */
  const save = (): void => {
    if (!named) {
      // Clicking a button that cannot act is how someone asks why. Answer by going to what is missing, on its
      // own step when Save was clicked from another one.
      if (step !== 3) {
        setPendingNameFlash(true);
        goTo(3);
      } else {
        // The name itself is what is missing, not a language's: the field goes back to it.
        setLang(null);
        flashName();
      }
      return;
    }
    // Nothing in the list: the games are where that is explained (the guide, or that nothing matches).
    if (!filled) {
      if (step !== 2) goTo(2);
      return;
    }
    if (!canSave) return;
    // A shape left as it was keeps the list's kind and layout exactly, a layout the hub does not offer included.
    const section = reshaped ? sectionOf(shape) : { kind: start.kind, layout: start.layout };
    // The name in each language as the step leaves it: the names shown for this very name, the owner's own over them;
    // the owner's own alone when those were another name's (the rest is translated as the list is stored).
    const listNames: ListNames = perLanguage ? { ...(namesNow ?? {}), ...ownNames } : {};
    onSave(
      auto
        ? { name: effectiveName, gameIds: autoOrder, ...section, mode: 'auto', filters: rule }
        : { name: effectiveName, gameIds: picked, ...section, mode: 'manual', filters: null },
      Object.keys(listNames).length > 0 ? listNames : undefined,
    );
  };

  /** Add's way on: Continue, then Create list. It is one button, so the second click of a double click (or tap) lands
   *  on what the first made of it: on Games it would refuse a list not yet built, and on Name and layout, the name
   *  suggested, it would create the list before the owner has seen its name and layout. The system counts the clicks
   *  of a double click (by its own time and distance), and a click counted past the first is ignored. iOS counts no
   *  taps (each is a click of its own, counted 1), so a press within a double tap's time (ANIM.DOUBLE_PRESS_MS) of
   *  the Continue that moved the step on is ignored too, on every step and whatever the name. */
  const wayOn = (event: React.MouseEvent<HTMLElement>): void => {
    if (event.detail > 1) return;
    if (movedOnAt.current !== null && event.timeStamp - movedOnAt.current < ANIM.DOUBLE_PRESS_MS) return;
    if (step === 3) save();
    else continueStep(event.timeStamp);
  };

  // The list whose tile is in the hand: the hand-picked games or the automatic ones.
  const dragging = sortable.draggedId ? sortable : autoSortable.draggedId ? autoSortable : null;
  const draggedGame = dragging?.draggedId ? gameById.get(dragging.draggedId) ?? null : null;

  const renderTile = (id: string): React.ReactElement => {
    const game = gameById.get(id);
    const isNew = editing && !startAuto && !startedWith.has(id);
    // An id the catalogue does not hold is still the owner's: it is shown, so it can be moved or removed
    // on purpose rather than carried through every save out of sight. While the catalogue is still loading
    // nothing is known yet, so the tile says so instead of calling the game unavailable; a catalogue that could not be
    // read knows nothing of it either, so the tile names it by its id.
    const title = game
      ? gameTitle(game)
      : loading
        ? t('Loading games…')
        : failed
          ? t('ID {{id}}', { id })
          : t('Unavailable game');
    const byline = game
      ? providerLabelOf(game, providerNames, t)
      : loading || failed
        ? ''
        : t('No longer in the catalogue');
    return (
      <S.Slot
        key={id}
        ref={(el) => {
          sortable.registerSlot(id)(el);
          if (el) slotEls.current.set(id, el);
          else slotEls.current.delete(id);
        }}
        draggable
        $dragging={sortable.draggedId === id}
        {...sortable.slotDragProps(id)}
      >
        <S.SlotBtns>
          <S.SlotBtn
            type="button"
            onClick={() => toggleGame(id)}
            aria-label={t('Remove {{name}} from list', { name: title })}
            title={t('Remove from list')}
          >
            <Icon name="close" size={13} />
          </S.SlotBtn>
        </S.SlotBtns>
        <S.Card $flash={flash?.id === id}>
          <S.Art $dim={!game}>
            <GameThumb game={game} render={(src) => <S.ArtImg src={src} alt="" loading="lazy" draggable={false} />} />
            {!game && !loading && !failed ? (
              <S.Missing>
                <Icon name="eye-off" size={16} />
                <S.MissingText text="Players don't see it. Remove it from the list." />
              </S.Missing>
            ) : null}
            {isNew ? <S.Tag $muted={false}>{t('New')}</S.Tag> : null}
          </S.Art>
          <S.TitleRow>
            <S.DragHandle aria-hidden="true">
              <Icon name="drag-handle" size={14} />
            </S.DragHandle>
            <S.TitleCol>
              <S.TileName $dim={!game} text={title} raw />
              <S.Provider text={byline} raw />
            </S.TitleCol>
          </S.TitleRow>
        </S.Card>
      </S.Slot>
    );
  };

  /** One of an automatic list's games: dragged into its order, never removed (its filters decide that). */
  const renderAutoTile = (game: GameSourceRecord): React.ReactElement => {
    const id = gameKey(game);
    return (
      <S.Slot
        key={id}
        ref={autoSortable.registerSlot(id)}
        role="listitem"
        aria-label={gameTitle(game)}
        draggable
        $dragging={autoSortable.draggedId === id}
        {...autoSortable.slotDragProps(id)}
      >
        <S.Card>
          <S.Art $dim={false}>
            <GameThumb game={game} render={(src) => <S.ArtImg src={src} alt="" loading="lazy" draggable={false} />} />
          </S.Art>
          <S.TitleRow>
            <S.DragHandle aria-hidden="true">
              <Icon name="drag-handle" size={14} />
            </S.DragHandle>
            <S.TitleCol>
              <S.TileName $dim={false} text={gameTitle(game)} raw />
              <S.Provider text={providerLabelOf(game, providerNames, t)} raw />
            </S.TitleCol>
          </S.TitleRow>
        </S.Card>
      </S.Slot>
    );
  };

  const listCount = auto ? autoGames.length : picked.length;

  /** The open facet's panel: under its button in the filter bar, or under its card (the second look). `active`: the step
   *  on show, which alone holds the refs (a step sliding out renders too, see the stage). */
  const renderMenu = (active: boolean, Panel: typeof S.Menu, style?: React.CSSProperties): React.ReactElement | null =>
    menu ? (
      <Panel ref={active ? menuRef : undefined} role="dialog" aria-label={t(facetTitle(menu))} style={style}>
        <S.MenuHead>
          <S.MenuTitle text={facetTitle(menu)} />
          <S.MenuHint text={menu === 'types' ? 'Open a type to pick its kinds' : 'Pick one or more'} />
        </S.MenuHead>
        <S.MenuList aria-busy={catalogPending}>
          {menu === 'providers' ? renderFlatOptions('providers', providers) : null}
          {menu === 'themes' ? renderFlatOptions('themes', themes) : null}
          {menu === 'types' ? (catalogPending ? renderOptionSkeleton() : renderTypeOptions()) : null}
        </S.MenuList>
        <S.MenuFoot>
          <S.TextAction type="button" onClick={() => setFacet(menu, [])}>
            <Text tag="span" text="Clear" />
          </S.TextAction>
          <S.SmallBtn type="button" $solid onClick={() => setMenu(null)}>
            {catalogPending
              ? t('Loading games…')
              : facetCount === 1
                ? t('Show 1 game')
                : t('Show {{n}} games', { n: facetCount })}
          </S.SmallBtn>
        </S.MenuFoot>
      </Panel>
    ) : null;

  // The parts a hand-picked list is made of, laid out side by side on a wider screen and in the accordion on a
  // phone.
  const renderFilterBar = (active: boolean): React.ReactElement => (
    <S.FilterBar ref={active ? barRef : undefined}>
      <S.Facets>
        {facetButtons.map((facet) => (
          <S.FacetBtn
            key={facet.id}
            type="button"
            $on={facet.picked > 0 || menu === facet.id}
            aria-expanded={menu === facet.id}
            aria-haspopup="dialog"
            onClick={(event) => {
              setMenuLeft(event.currentTarget.offsetLeft);
              setMenu(menu === facet.id ? null : facet.id);
              setMenuQuery('');
            }}
          >
            <Text tag="span" text={facet.label} />
            {facet.picked > 0 ? <S.FacetCount>{facet.picked}</S.FacetCount> : null}
            <Icon name="chevron-down" size={13} />
          </S.FacetBtn>
        ))}
      </S.Facets>
      {chips.length > 0 ? (
        <S.Chips>
          {chips.map((chip) => (
            <S.FilterChip
              key={chip.key}
              type="button"
              onClick={chip.remove}
              aria-label={t('Remove filter {{name}}', { name: chip.label })}
            >
              <S.ChipLabel text={chip.label} raw />
              <Icon name="close" size={11} />
            </S.FilterChip>
          ))}
          <S.TextAction
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, providers: [], types: [], themes: [] }))}
          >
            <Text tag="span" text="Clear all" />
          </S.TextAction>
        </S.Chips>
      ) : null}
      {renderMenu(active, S.Menu, isPhone ? undefined : { left: `${menuLeft}px` })}
    </S.FilterBar>
  );

  const search = (
    <S.Search
      type="search"
      value={filters.query}
      onChange={(e) => {
        const query = e.target.value;
        setFilters((prev) => ({ ...prev, query }));
      }}
      placeholder={t('Search games by name')}
      aria-label={t('Search games by name')}
    />
  );

  const resultCount = (
    <S.SubLabel text={matches.length === 1 ? '1 game' : '{{n}} games'} placeholders={{ n: matches.length }} />
  );

  const addAllBtn = canAddAll ? (
    <S.SmallBtn type="button" onClick={addAll}>
      <Icon name="plus" size={12} />
      <Text tag="span" text="Add all {{n}}" placeholders={{ n: notInList.length }} />
    </S.SmallBtn>
  ) : null;

  // Said where the games would be when the catalogue could not be read, with a way to read it again: the failed read
  // leaves the catalogue empty, and "No games match" would put that down to the filters.
  const catalogueFailed = (
    <>
      <S.Note text="The game catalogue could not be loaded, so this list's games cannot be shown. Please try again." />
      <S.MoreRow>
        <Button variant="outline" label={t('Try again')} onClick={() => setCatalogAttempt((n) => n + 1)} />
      </S.MoreRow>
    </>
  );

  const resultRows = (
    <S.Rows>
      {loading && matches.length === 0 ? <S.Note text="Loading games…" /> : null}
      {!loading && matches.length === 0 ? (
        failed ? (
          catalogueFailed
        ) : (
          <S.Note text="No games match these filters." />
        )
      ) : null}
      {shown.map((game) => {
        const id = gameKey(game);
        const inList = pickedIds.has(id);
        const title = gameTitle(game);
        return (
          <S.Row
            key={id}
            type="button"
            $in={inList}
            // A toggle: the same name either way, pressed while the game is in the list. The tile's
            // X on the right is what is named "Remove ... from list".
            aria-pressed={inList}
            aria-label={t('Add {{name}} to list', { name: title })}
            onClick={() => toggleGame(id)}
          >
            <S.RowArt>
              <GameThumb game={game} render={(src) => <S.ArtImg src={src} alt="" loading="lazy" draggable={false} />} />
            </S.RowArt>
            <S.RowText>
              <S.RowName text={title} raw />
              <S.Provider text={providerLabelOf(game, providerNames, t)} raw />
            </S.RowText>
            <S.Pill $in={inList}>
              <Icon name={inList ? 'check' : 'plus'} size={12} />
              <Text tag="span" text={inList ? 'Added' : 'Add'} />
            </S.Pill>
          </S.Row>
        );
      })}
      {visible < matches.length ? (
        <S.MoreRow>
          <Button
            variant="outline"
            label={t('Load more ({{shown}} of {{total}})', {
              shown: shown.length,
              total: matches.length,
            })}
            onClick={() => setVisible((v) => v + PAGE)}
          />
        </S.MoreRow>
      ) : null}
    </S.Rows>
  );

  const removeAllBtn =
    picked.length > 0 ? (
      <S.TextAction type="button" $danger onClick={() => setPicked([])}>
        <Text tag="span" text="Remove all" />
      </S.TextAction>
    ) : null;

  const pickedCount = (
    <S.CountWrap>
      <S.Count>{listCount}</S.Count>
      {countPing !== null ? (
        <S.CountPing key={countPing} aria-hidden="true">
          {listCount}
        </S.CountPing>
      ) : null}
    </S.CountWrap>
  );

  const pickedList = (
    <S.ListScroll>
      {picked.length === 0 ? (
        <S.Guide>
          <S.GuideTitle text="Your list is empty" />
          <S.GuideText text="Games you add from the results appear here. You can remove them any time before saving." />
        </S.Guide>
      ) : (
        <S.Thumbs onDragOver={sortable.onGridDragOver} onDrop={(event) => event.preventDefault()}>
          {picked.map(renderTile)}
        </S.Thumbs>
      )}
    </S.ListScroll>
  );

  /** One segment of the phone accordion: its header opens it (closing the other), its body is what it holds.
   *  One is always open, so the open one's header has nothing to do and says so (`aria-disabled`); its action
   *  (Add all, Remove all) sits beside it, outside the button. */
  const renderSegment = (
    id: 'results' | 'selected',
    title: React.ReactNode,
    action: React.ReactNode,
    body: React.ReactNode,
    active: boolean,
  ): React.ReactElement => {
    const open = segment === id;
    return (
      <S.Segment $open={open}>
        <S.SegmentHead>
          {/* The results' header leads the Games step on a phone, so Back and the stepper leave the keyboard there. */}
          <S.SegmentHeading
            ref={active && id === 'results' ? stepHeadRef : undefined}
            tabIndex={id === 'results' ? -1 : undefined}
          >
            <S.SegmentToggle
              type="button"
              id={`${segmentId}${id}`}
              aria-expanded={open}
              aria-controls={`${segmentId}${id}-body`}
              aria-disabled={open}
              onClick={open ? undefined : () => openSegment(id)}
            >
              <S.SegmentChevron $open={open} aria-hidden="true">
                <Icon name="chevron-down" size={14} />
              </S.SegmentChevron>
              {title}
            </S.SegmentToggle>
          </S.SegmentHeading>
          {open ? action : null}
        </S.SegmentHead>
        {/* A closed body stays in place, only folded away, so the results keep where they were scrolled to. */}
        <S.SegmentBody id={`${segmentId}${id}-body`} role="region" aria-labelledby={`${segmentId}${id}`} $open={open}>
          {body}
        </S.SegmentBody>
      </S.Segment>
    );
  };

  // The way on: Continue (naming the method on its own step), then Create list. Literal strings, so the extractor
  // harvests each.
  const nextLabel =
    step === 1
      ? auto
        ? t('Continue with Automatic')
        : market
          ? t('Continue with the market')
          : t('Continue with Hand-picked')
      : step === 2
        ? t('Continue')
        : busy
          ? t('Saving…')
          : t('Create list');

  // The second look's automatic Games step, and a list from the market's Pick a collection step (in either look), say
  // what they wait for before Continue is pressed, and Continue looks unavailable until then (it still refuses, and says
  // why in the live region, as it always does). A catalogue that could not be read is what they wait for then. While it
  // loads the automatic step says nothing (its matches are not known yet); the market step still waits for its pick.
  const waiting =
    ((v2 && auto && !loading) || market) && step === 2 && !filled
      ? failed
        ? t("Couldn't load")
        : market
          ? t('Pick a collection to continue')
          : hasRule
            ? t('No games match these filters yet')
            : t('Pick at least one to continue')
      : null;

  const footer = editing ? (
    <S.FooterRow>
      {onDelete ? (
        confirmingDelete ? (
          <S.FooterActions>
            <S.FooterNote text="Delete this list?" />
            <Button variant="text" label={t('Keep it')} onClick={() => setConfirmingDelete(false)} />
            <S.DeleteBtn type="button" disabled={busy} aria-busy={busy} onClick={busy ? undefined : onDelete}>
              {/* Literal strings per branch, so the extractor harvests both. */}
              {busy ? <Text tag="span" text="Deleting…" /> : <Text tag="span" text="Delete" />}
            </S.DeleteBtn>
          </S.FooterActions>
        ) : (
          <S.DeleteBtn type="button" onClick={() => setConfirmingDelete(true)}>
            <Text tag="span" text="Delete list" />
          </S.DeleteBtn>
        )
      ) : (
        <span />
      )}
      <S.FooterActions>
        <S.Changes $dirty={dirty} text={dirty ? changes.join(' · ') : t('No changes')} raw />
        <Button variant="text" label={t('Cancel')} onClick={dismiss} />
        <S.PrimaryBtn
          variant="primary"
          label={busy ? t('Saving…') : t('Save changes')}
          onClick={save}
          $off={!canSave}
        />
      </S.FooterActions>
    </S.FooterRow>
  ) : (
    <S.FooterRow>
      {step > 1 ? <Button variant="text" label="Back" onClick={back} /> : <span />}
      {/* A list from the market: the collection picked, said beside the way on. */}
      {market && step === 2 && pickedCollection ? (
        <S.Picked>
          <S.PickedTag text="Selected" />
          <S.PickedName>{pickedCollection.name}</S.PickedName>
          <S.PickedMeta>
            {[
              collectionPlace(pickedCollection),
              picked.length === 1 ? t('1 game') : t('{{n}} games', { n: picked.length }),
            ]
              .filter((part) => part !== '')
              .join(' · ')}
          </S.PickedMeta>
        </S.Picked>
      ) : null}
      <S.FooterActions>
        {/* Mounted with the step that can refuse, before it has anything to say: a live region that arrives together
            with its words is often not read out. */}
        {step === 2 ? <S.FooterNotice role="status">{notice && !filled ? notice : null}</S.FooterNotice> : null}
        {/* The hint (`waiting`), said before Continue is pressed: beside the live region, not in it, so that a refusal
            still changes the region's words and is read out. It gives way to the refusal. */}
        {waiting !== null && !(notice && !filled) ? <S.FooterNotice>{waiting}</S.FooterNotice> : null}
        <Button variant="text" label={t('Cancel')} onClick={dismiss} />
        {/* One element on every step, so the keyboard is never dropped on <body> by a button swapped out under it
            (moving on, Continue takes it to the new step's heading). */}
        <S.PrimaryBtn
          variant="primary"
          label={nextLabel}
          onClick={wayOn}
          $off={(step === 3 && !canSave) || waiting !== null}
        />
      </S.FooterActions>
    </S.FooterRow>
  );

  // Step 1: the two ways of building, as two cards with radio semantics. Each radio is visually hidden and its card
  // is its label, so the whole card picks it.
  const renderMethodStep = (active: boolean): React.ReactElement => (
    <S.StepPane>
      <S.ModeHeading id={methodHeadingId} ref={active ? stepHeadRef : undefined} tabIndex={-1}>
        <Text tag="span" text="How should this list be built?" />
      </S.ModeHeading>
      <S.ModeSet aria-labelledby={methodHeadingId} $three={offerMarket}>
        <S.ModeCards $three={offerMarket}>
          <S.ModeCard $on={auto}>
            <S.ModeRadio
              type="radio"
              name="list-mode"
              value="auto"
              checked={auto}
              onChange={() => {
                setListMode('auto');
                setFromMarket(false);
                // Back on the market, the collection is picked again: what the list holds may have changed since.
                setPickedCollection(null);
              }}
              aria-labelledby={`${modeId}auto-name`}
              aria-describedby={`${modeId}auto-hint ${modeId}auto-lines ${modeId}auto-badge`}
            />
            <S.ModeIcon $on={auto} aria-hidden="true">
              <Icon name="filter" size={22} />
            </S.ModeIcon>
            <S.ModeText>
              <S.ModeName id={`${modeId}auto-name`}>
                <Text tag="span" text="Automatic, by filters" />
              </S.ModeName>
              <S.ModeHint id={`${modeId}auto-hint`}>
                <Text
                  tag="span"
                  text="Pick providers, types or themes. Every matching game is added, and new ones join on their own."
                />
              </S.ModeHint>
              <S.ModeLines id={`${modeId}auto-lines`} $on={auto}>
                <S.ModeLine>
                  <Icon name="check" size={16} />
                  <Text tag="span" text="Stays up to date without edits" />
                </S.ModeLine>
                <S.ModeLine>
                  <Icon name="check" size={16} />
                  <Text tag="span" text="Three steps, under a minute" />
                </S.ModeLine>
                <S.ModeLine>
                  <Icon name="check" size={16} />
                  <Text tag="span" text="The games can be reordered by hand" />
                </S.ModeLine>
              </S.ModeLines>
            </S.ModeText>
            {/* Last in reading order, as the last of what describes the radio; the card's grid sets it at the top
                right. */}
            <S.ModeBadge id={`${modeId}auto-badge`}>
              <Text tag="span" text="Recommended" />
            </S.ModeBadge>
          </S.ModeCard>
          <S.ModeCard $on={!auto && !market}>
            <S.ModeRadio
              type="radio"
              name="list-mode"
              value="manual"
              checked={!auto && !market}
              onChange={() => {
                setListMode('manual');
                setFromMarket(false);
                // Back on the market, the collection is picked again: the hand-picked games may change meanwhile.
                setPickedCollection(null);
                setSegment('results');
              }}
              aria-labelledby={`${modeId}manual-name`}
              aria-describedby={`${modeId}manual-hint ${modeId}manual-lines`}
            />
            <S.ModeIcon $on={!auto && !market} aria-hidden="true">
              <HandGlyph />
            </S.ModeIcon>
            <S.ModeText>
              <S.ModeName id={`${modeId}manual-name`}>
                <Text tag="span" text="Hand-picked" />
              </S.ModeName>
              <S.ModeHint id={`${modeId}manual-hint`}>
                <Text
                  tag="span"
                  text="Search the catalogue and choose every game yourself. The list changes only when you edit it."
                />
              </S.ModeHint>
              <S.ModeLines id={`${modeId}manual-lines`} $on={!auto && !market}>
                <S.ModeLine>
                  <Icon name="check" size={16} />
                  <Text tag="span" text="Full control over every game" />
                </S.ModeLine>
                <S.ModeLine>
                  <Icon name="check" size={16} />
                  <Text tag="span" text="Your own order from the start" />
                </S.ModeLine>
                <S.ModeLine>
                  <Icon name="check" size={16} />
                  <Text tag="span" text="Good for promos and seasonal picks" />
                </S.ModeLine>
              </S.ModeLines>
            </S.ModeText>
          </S.ModeCard>
          {offerMarket ? (
            <S.ModeCard $on={market}>
              <S.ModeRadio
                type="radio"
                name="list-mode"
                value="market"
                checked={market}
                onChange={() => {
                  setListMode('manual');
                  setFromMarket(true);
                  setSegment('selected');
                }}
                aria-labelledby={`${modeId}market-name`}
                aria-describedby={`${modeId}market-hint ${modeId}market-lines`}
              />
              <S.ModeIcon $on={market} aria-hidden="true">
                <LumioGlyphOutline size={22} />
              </S.ModeIcon>
              <S.ModeText>
                <S.ModeName id={`${modeId}market-name`}>
                  <Text tag="span" text="From the market" />
                </S.ModeName>
                <S.ModeHint id={`${modeId}market-hint`}>
                  <Text
                    tag="span"
                    text="Start from a collection inspired by casinos in your market, then make it your own."
                  />
                </S.ModeHint>
                <S.ModeLines id={`${modeId}market-lines`} $on={market}>
                  <S.ModeLine>
                    <Icon name="check" size={16} />
                    <Text tag="span" text="A ready-made starting point" />
                  </S.ModeLine>
                  <S.ModeLine>
                    <Icon name="check" size={16} />
                    <Text tag="span" text="Easy to adjust, game by game" />
                  </S.ModeLine>
                  <S.ModeLine>
                    <Icon name="check" size={16} />
                    <Text tag="span" text="Saved as a hand-picked list" />
                  </S.ModeLine>
                </S.ModeLines>
              </S.ModeText>
            </S.ModeCard>
          ) : null}
        </S.ModeCards>
      </S.ModeSet>
    </S.StepPane>
  );

  // Step 3: what the list is called, and how it shows on the page: one column in the middle of the editor, the name
  // block (its heading, the field, the hint and its actions) over the layout block (its heading, the three cards).
  const renderNameStep = (active: boolean): React.ReactElement => (
    <S.StepPane>
      <S.StepColumn>
        <S.StepBlock>
          <S.StepTitle ref={active ? stepHeadRef : undefined} tabIndex={-1}>
            <Text tag="span" text="Name your list" />
          </S.StepTitle>
          <S.NameWrap ref={active ? nameWrapRef : undefined} $flash={nameFlash}>
            <Field
              label={lang === null ? 'List name' : t('List name in {{language}}', { language: adLanguageLabel(lang) })}
              value={lang === null ? shownName : nameIn(lang)}
              onChange={(value) => {
                if (lang !== null) {
                  setTypedNames((prev) => ({ ...prev, [lang]: value }));
                  return;
                }
                setName(value);
                setNameTouched(true);
              }}
              // The field translates its placeholder, so the suggestion (the title the site shows, English) goes in a
              // sentence of the hub's: handed over alone it was looked up as a key ("Sports" showed as "Deportes").
              placeholder={
                lang !== null
                  ? asking
                    ? t('Loading…')
                    : undefined
                  : auto && !editing && !following
                    ? t('Suggested: {{name}}', { name: suggestion })
                    : t('For example: My hot picks')
              }
              suffix={lang === null && following && suggestion !== '' ? t('Suggested') : undefined}
              // A language's name as the service takes it (a translation can be longer than the name).
              maxLength={lang === null ? 60 : LANGUAGE_NAME_MAX}
            />
          </S.NameWrap>
          {perLanguage ? (
            <LangLine role="group" aria-label={t('Languages')}>
              {languages.map((code) => {
                // EN edits the name itself.
                const target = code === 'en' ? null : code;
                return (
                  <LangChip
                    key={code}
                    type="button"
                    $on={lang === target}
                    aria-pressed={lang === target}
                    title={adLanguageLabel(code)}
                    onClick={() => pickLanguage(target)}
                  >
                    {code.toUpperCase()}
                  </LangChip>
                );
              })}
            </LangLine>
          ) : null}
          {auto && !editing ? (
            <S.NameRow>
              <S.NameHint text="Suggested from your filters. Type to use your own." />
              {nameTouched ? (
                <S.TextAction
                  type="button"
                  onClick={() => {
                    setName('');
                    setNameTouched(false);
                    setLang(null);
                  }}
                >
                  <Text tag="span" text="Reset to suggested" />
                </S.TextAction>
              ) : null}
            </S.NameRow>
          ) : null}
          {auto && editing && suggestion !== '' && suggestion !== name.trim() ? (
            <S.NameRow>
              <S.TextAction
                type="button"
                onClick={() => {
                  setName(suggestion);
                  setNameTouched(true);
                  setLang(null);
                }}
              >
                <Text tag="span" text="Use suggested name" />
              </S.TextAction>
            </S.NameRow>
          ) : null}
        </S.StepBlock>
        {layoutChoice ? (
          <S.StepBlock>
            <S.StepTitle>
              <Text tag="span" text="Choose a layout" />
            </S.StepTitle>
            <ListLayoutPicker value={shape} onChange={setShape} />
          </S.StepBlock>
        ) : null}
      </S.StepColumn>
    </S.StepPane>
  );

  // The automatic Games step's three filter cards (the second look): what each filter is, its example and its button
  // while nothing is picked; what each holds once anything is. Literal t() per string, so the extractor harvests each.
  const filterCards: ReadonlyArray<{
    id: Facet;
    title: string;
    icon: React.ReactElement;
    what: string;
    example: string;
    cta: string;
    any: string;
  }> = [
    {
      id: 'providers',
      title: t('Provider'),
      icon: <StudioGlyph />,
      what: t('The studio that makes the games.'),
      example: t('For example Hacksaw, Pragmatic, Spribe'),
      cta: t('Choose providers'),
      any: t('Any provider'),
    },
    {
      id: 'types',
      title: t('Type'),
      icon: <Icon name="grid" size={20} />,
      what: t('The kind of game.'),
      example: t('For example Slots, Crash, Live casino'),
      cta: t('Choose types'),
      any: t('Any type'),
    },
    {
      id: 'themes',
      title: t('Theme'),
      icon: <Icon name="palette" size={20} />,
      what: t('What the games are about.'),
      example: t('For example Halloween, Egypt, Fruits'),
      cta: t('Choose themes'),
      any: t('Any theme'),
    },
  ];

  /** What a card holds: its "Any …" while empty, its pick, or its first pick and how many more. */
  const cardValue = (id: Facet, any: string): string => {
    const picks = filters[id];
    if (picks.length === 0) return any;
    const first = pickLabel(id, picks[0]);
    return picks.length === 1 ? first : t('{{name}} +{{n}}', { name: first, n: picks.length - 1 });
  };

  const toggleCard = (id: Facet): void => {
    setMenu(menu === id ? null : id);
    setMenuQuery('');
  };

  const clearFilters = (): void => {
    setFilters((prev) => ({ ...prev, providers: [], types: [], themes: [] }));
    setNotice(null);
    setMenu(null);
  };

  /** An automatic list's games once it has a filter, in either look (`cards`: the second look's): the note while the
   *  catalogue loads, the note when nothing matches, else the games (dragged into the owner's order) and Show more. */
  const renderAutoGames = (cards: boolean): React.ReactElement => {
    if (loading && games.length === 0) return <S.Note text="Loading games…" />;
    if (failed) return catalogueFailed;
    if (autoGames.length === 0) {
      return cards ? (
        <S.CardsNone text="No games match these filters yet. Matching games will join as soon as they are released." />
      ) : (
        <S.Note text="No games match these filters yet. Matching games will join as soon as they are released." />
      );
    }
    const grid = {
      role: 'list',
      'aria-label': t('Games in this list'),
      onDragOver: autoSortable.onGridDragOver,
      onDrop: (event: React.DragEvent<HTMLDivElement>) => event.preventDefault(),
    };
    return (
      <>
        {cards ? (
          <S.RisingThumbs {...grid} $rise={risePicked}>
            {autoShown.map(renderAutoTile)}
          </S.RisingThumbs>
        ) : (
          <S.Thumbs {...grid}>{autoShown.map(renderAutoTile)}</S.Thumbs>
        )}
        {autoVisible < autoGames.length ? (
          <S.MoreRow>
            <Button
              variant="outline"
              label={t('Show more ({{shown}} of {{total}})', {
                shown: autoShown.length,
                total: autoGames.length,
              })}
              onClick={() => setAutoVisible((v) => v + AUTO_PAGE)}
            />
          </S.MoreRow>
        ) : null}
      </>
    );
  };

  const renderAutoCards = (active: boolean): React.ReactElement => (
    <>
      <S.CardsQuestion ref={active ? stepHeadRef : undefined} tabIndex={-1}>
        <Text tag="span" text="Which games should be in this list?" />
      </S.CardsQuestion>
      <S.FilterCards ref={active ? barRef : undefined}>
        {filterCards.map((card, index) => {
          const on = filters[card.id].length > 0;
          const open = menu === card.id;
          return (
            <S.FilterCard key={card.id} data-filter-card="" $filled={hasRule} $on={on} $open={open}>
              <S.FilterHead
                type="button"
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={() => toggleCard(card.id)}
              >
                <S.FilterIcon data-filter-icon="" $filled={hasRule} $on={on} aria-hidden="true">
                  {card.icon}
                </S.FilterIcon>
                <S.FilterWords>
                  <S.FilterTitle $filled={hasRule} text={card.title} raw />
                  <S.FilterValue $filled={hasRule} text={cardValue(card.id, card.any)} raw />
                </S.FilterWords>
                <S.FilterChevron $filled={hasRule} aria-hidden="true">
                  <Icon name="chevron-down" size={14} />
                </S.FilterChevron>
              </S.FilterHead>
              <S.FilterMore $filled={hasRule}>
                <S.FilterWhat text={card.what} raw />
                <S.FilterExample text={card.example} raw />
                <S.FilterCta type="button" tabIndex={hasRule ? -1 : undefined} onClick={() => toggleCard(card.id)}>
                  <Text tag="span" text={card.cta} raw />
                  <Icon name="chevron-down" size={14} />
                </S.FilterCta>
              </S.FilterMore>
              {/* The last card's panel hangs from its right edge, so it stays in the editor where cards are narrow. */}
              {open ? renderMenu(active, index === filterCards.length - 1 ? S.CardMenuEnd : S.CardMenu) : null}
            </S.FilterCard>
          );
        })}
      </S.FilterCards>
      {!hasRule ? (
        <S.CardsNote text="Pick at least one of the three above. You can combine them, and every game that matches shows up here." />
      ) : (
        <S.Body>
          <S.Pane aria-label={t('Your list')}>
            <S.CardsHead $rise={risePicked}>
              <S.ListTitle>
                <Text tag="span" text="Matching games" />
              </S.ListTitle>
              <S.MatchCount data-match-count="">{listCount}</S.MatchCount>
              <S.Spacer />
              <S.TextAction type="button" onClick={clearFilters}>
                <Text tag="span" text="Clear all" />
              </S.TextAction>
            </S.CardsHead>
            <S.ListScroll $flow>{renderAutoGames(true)}</S.ListScroll>
          </S.Pane>
        </S.Body>
      )}
    </>
  );

  /** The Games step. `active`: the step on show, which alone holds the refs (a step sliding out renders too). */
  const renderGamesStep = (active: boolean): React.ReactElement =>
    loadMarketCollections && market ? (
      // A list from the market: its step 2 is picking the collection it starts from.
      <MarketCollectionPicker
        load={loadMarketCollections}
        has={(id) => gameById.has(id)}
        catalogueReady={!loading && !failed}
        catalogueFailed={failed}
        onRetryCatalogue={() => setCatalogAttempt((n) => n + 1)}
        gameOf={(id) => gameById.get(id)}
        gameName={(id) => {
          const game = gameById.get(id);
          return game ? gameTitle(game) : undefined;
        }}
        selectedSourceId={pickedCollection?.sourceId}
        onPick={pickCollection}
        headingRef={active ? stepHeadRef : undefined}
      />
    ) : v2 && auto ? (
      renderAutoCards(active)
    ) : isPhone && !auto ? (
      // A hand-picked list on a phone: the results (under their filters and search) and the list, one open at
      // a time, filling the sheet. The results start open, and scroll from the first swipe.
      <S.Accordion>
        {renderSegment(
          'results',
          <>
            <S.SegmentName text="Results" />
            {resultCount}
          </>,
          addAllBtn,
          <>
            {renderFilterBar(active)}
            {search}
            {resultRows}
          </>,
          active,
        )}
        {renderSegment(
          'selected',
          <>
            <S.SegmentName text="Selected" />
            {pickedCount}
          </>,
          removeAllBtn,
          pickedList,
          active,
        )}
      </S.Accordion>
    ) : (
      <>
        {/* One filter bar for both ways of building, in the same place with the same picks. */}
        <S.FilterDock>{renderFilterBar(active)}</S.FilterDock>

        <S.Body>
          {auto ? (
            <S.Pane aria-label={t('Your list')}>
              <S.PaneHead>
                <S.ListTitle ref={active ? stepHeadRef : undefined} tabIndex={-1}>
                  <Text tag="span" text="Matching games" />
                </S.ListTitle>
                <S.Count>{listCount}</S.Count>
                {hasRule ? <S.SubLabel text="New matching games join automatically." /> : null}
              </S.PaneHead>
              <S.ListScroll $flow>
                {!hasRule ? (
                  <S.Guide>
                    <Icon name="filter" size={28} />
                    <S.GuideTitle text="Choose at least one filter" />
                    <S.GuideText text="Every game that matches appears here, and new games that match join on their own." />
                  </S.Guide>
                ) : (
                  renderAutoGames(false)
                )}
              </S.ListScroll>
            </S.Pane>
          ) : (
            <>
              <S.Results aria-label={t('Results')}>
                {search}
                <S.PaneHead>
                  <S.ListTitle ref={active ? stepHeadRef : undefined} tabIndex={-1}>
                    <Text tag="span" text="Results" />
                  </S.ListTitle>
                  {resultCount}
                  <S.Spacer />
                  {addAllBtn}
                </S.PaneHead>
                {resultRows}
              </S.Results>

              <S.Pane aria-label={t('Your list')}>
                <S.PaneHead>
                  <S.ListTitle>
                    <Text tag="span" text="In this list" />
                  </S.ListTitle>
                  {pickedCount}
                  <S.Spacer />
                  {removeAllBtn}
                </S.PaneHead>
                {pickedList}
              </S.Pane>
            </>
          )}
        </S.Body>
      </>
    );

  const renderStep = (n: WizardStep, active: boolean): React.ReactElement =>
    n === 1 ? renderMethodStep(active) : n === 3 ? renderNameStep(active) : renderGamesStep(active);

  return (
    <Sheet
      header={
        <S.WizardHead>
          <SheetTitle text={editing ? 'Configure list' : 'Create a new list'} tag="span" />
          <ListWizardStepper
            step={step}
            allClickable={editing}
            summaries={{
              method: auto ? t('Automatic') : market ? t('From the market') : t('Hand-picked'),
              games:
                market && pickedCollection
                  ? pickedCollection.name
                  : listCount === 1
                    ? t('1 game')
                    : t('{{n}} games', { n: listCount }),
            }}
            gamesStepName={market ? t('Pick a collection') : undefined}
            onStep={jumpTo}
            pills={v2}
            layoutStep={layoutChoice}
          />
        </S.WizardHead>
      }
      ariaLabel={editing ? 'Configure list' : 'Create a new list'}
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="1240px"
      // Taller than a sheet's usual 720px: under the usual cap the Games step's list had room for less than one row
      // of cards.
      maxHeight="min(920px, 100%)"
      footer={footer}
    >
      {/* Published as a custom property, not as `height`: an inline height would outrank the stylesheet's
          mobile rule (`height: auto`) and lock the stacked phone layout into a desktop-derived pixel height.
          Every step renders inside this one element, so what it measures holds across them. */}
      <S.Editor
        ref={editorRef}
        style={
          editorHeight === null
            ? undefined
            : ({
                '--editor-h': `${editorHeight}px`,
                ...(splitHeight === null ? {} : { '--split-h': `${splitHeight}px` }),
              } as React.CSSProperties)
        }
      >
        {v2 ? (
          // Each step in a layer of its own: while one slides in, the one it replaces slides out over it, out of reach
          // and out of the accessibility tree, then is taken away (see `leaving`). Keyed by step, so a step that turns
          // back before it is gone is the same one sliding back, not a copy. The one on show comes first, so an id
          // both carry for that moment names the one on show.
          <S.StepStage data-step-stage="" $sliding={leaving !== null}>
            <S.StepLayer
              key={`step-${step}`}
              ref={NOT_INERT}
              data-step-layer=""
              data-phase={leaving ? 'enter' : 'rest'}
              $phase={leaving ? 'enter' : 'rest'}
              $dir={leaving?.dir ?? 'forward'}
            >
              {renderStep(step, true)}
            </S.StepLayer>
            {leaving && leaving.step !== step ? (
              <S.StepLayer
                key={`step-${leaving.step}`}
                ref={INERT}
                aria-hidden="true"
                data-step-layer=""
                data-phase="exit"
                $phase="exit"
                $dir={leaving.dir}
              >
                {renderStep(leaving.step, false)}
              </S.StepLayer>
            ) : null}
          </S.StepStage>
        ) : (
          renderStep(step, true)
        )}
      </S.Editor>
      {draggedGame && dragging
        ? createPortal(
            <S.DragLayer ref={dragging.dragLayerRef} aria-hidden="true" style={{ transform: dragging.ghostTransform }}>
              <S.DragGhost style={{ width: `${dragging.ghostWidth}px` }}>
                <S.Art $dim={false}>
                  {/* The tile in the hand was drawn a moment ago: its art is read already. */}
                  <GameThumb game={draggedGame} render={(src) => <S.ArtImg src={src} alt="" draggable={false} />} />
                </S.Art>
                <S.TitleRow>
                  <S.DragHandle aria-hidden="true">
                    <Icon name="drag-handle" size={14} />
                  </S.DragHandle>
                  <S.TitleCol>
                    <S.TileName $dim={false} text={gameTitle(draggedGame)} raw />
                    <S.Provider text={providerLabelOf(draggedGame, providerNames, t)} raw />
                  </S.TitleCol>
                </S.TitleRow>
              </S.DragGhost>
            </S.DragLayer>,
            document.body,
          )
        : null}
    </Sheet>
  );
};

export default CustomListEditor;
